import React, { useState, useMemo } from 'react';
import './DashboardUsers.css';
import { API_BASE } from '../utils/apiBase';

function DashboardUsers({ users = [], onRefresh, token, currentUserId }) {
  const [searchTerm, setSearchTerm] = useState('');
  const [filterRole, setFilterRole] = useState('all');
  const [filterSubscription, setFilterSubscription] = useState('all');
  const [sortBy, setSortBy] = useState('date-desc');
  const [showEditModal, setShowEditModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  const [editForm, setEditForm] = useState({
    name: '',
    email: '',
    phone: '',
    company: '',
    role: 'user',
    subscription: 'none'
  });
  const [createForm, setCreateForm] = useState({
    name: '',
    email: '',
    password: '',
    phone: '',
    company: '',
    role: 'user',
    subscription: 'none'
  });
  const [isLoading, setIsLoading] = useState(false);
  const [message, setMessage] = useState(null);

  // Garantir que users seja sempre um array
  const safeUsers = Array.isArray(users) ? users : [];

  // Filtrar e ordenar usuários
  const filteredAndSortedUsers = useMemo(() => {
    let result = [...safeUsers];

    // Filtro de busca
    if (searchTerm) {
      const searchLower = searchTerm.toLowerCase();
      result = result.filter(user => 
        (user.email || '').toLowerCase().includes(searchLower) ||
        (user.name || '').toLowerCase().includes(searchLower) ||
        (user.company || '').toLowerCase().includes(searchLower) ||
        (user.id?.toString() || '').includes(searchLower)
      );
    }

    // Filtro por role
    if (filterRole !== 'all') {
      result = result.filter(user => user.role === filterRole);
    }

    // Filtro por subscription
    if (filterSubscription !== 'all') {
      result = result.filter(user => user.subscription === filterSubscription);
    }

    // Ordenação
    result.sort((a, b) => {
      switch (sortBy) {
        case 'date-desc':
          return new Date(b.createdAt) - new Date(a.createdAt);
        case 'date-asc':
          return new Date(a.createdAt) - new Date(b.createdAt);
        case 'name':
          return (a.name || a.email).localeCompare(b.name || b.email);
        case 'email':
          return a.email.localeCompare(b.email);
        case 'role':
          return (a.role || '').localeCompare(b.role || '');
        case 'subscription':
          return (a.subscription || '').localeCompare(b.subscription || '');
        default:
          return 0;
      }
    });

    return result;
  }, [safeUsers, searchTerm, filterRole, filterSubscription, sortBy]);

  // Abrir modal de edição
  const handleEditClick = (user) => {
    setSelectedUser(user);
    setEditForm({
      name: user.name || '',
      email: user.email || '',
      phone: user.phone || '',
      company: user.company || '',
      role: user.role || 'user',
      subscription: user.subscription || 'none'
    });
    setShowEditModal(true);
  };

  // Abrir modal de criação
  const handleCreateClick = () => {
    setCreateForm({
      name: '',
      email: '',
      password: '',
      phone: '',
      company: '',
      role: 'user',
      subscription: 'none'
    });
    setShowCreateModal(true);
  };

  // Criar novo usuário
  const handleCreateUser = async () => {
    if (!createForm.email || !createForm.password) {
      setMessage({ type: 'error', text: 'Email e senha são obrigatórios' });
      return;
    }

    setIsLoading(true);
    setMessage(null);

    try {
      const response = await fetch(`${API_BASE}/api/admin/users`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(createForm)
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.error || 'Falha ao criar usuário');
      }

      setMessage({ type: 'success', text: 'Usuário criado com sucesso!' });
      setShowCreateModal(false);
      if (onRefresh) onRefresh();
    } catch (error) {
      setMessage({ type: 'error', text: error.message });
    } finally {
      setIsLoading(false);
    }
  };

  // Atualizar usuário
  const handleUpdateUser = async () => {
    if (!selectedUser) return;
    
    setIsLoading(true);
    setMessage(null);

    try {
      const response = await fetch(`${API_BASE}/api/admin/users/${selectedUser.id}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(editForm)
      });

      if (!response.ok) {
        throw new Error('Falha ao atualizar usuário');
      }

      setMessage({ type: 'success', text: 'Usuário atualizado com sucesso!' });
      setShowEditModal(false);
      setSelectedUser(null);
      if (onRefresh) onRefresh();
    } catch (error) {
      setMessage({ type: 'error', text: error.message });
    } finally {
      setIsLoading(false);
    }
  };

  // Confirmar exclusão
  const handleDeleteClick = (user) => {
    // Impedir que o admin delete a si mesmo
    if (currentUserId && user.id === currentUserId) {
      setMessage({ type: 'error', text: 'Você não pode deletar sua própria conta!' });
      return;
    }
    setSelectedUser(user);
    setShowDeleteModal(true);
  };

  // Deletar usuário
  const confirmDelete = async () => {
    if (!selectedUser) return;

    setIsLoading(true);
    setMessage(null);

    try {
      const response = await fetch(`${API_BASE}/api/admin/users/${selectedUser.id}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.error || 'Falha ao excluir usuário');
      }

      setMessage({ type: 'success', text: 'Usuário excluído com sucesso!' });
      setShowDeleteModal(false);
      setSelectedUser(null);
      if (onRefresh) onRefresh();
    } catch (error) {
      setMessage({ type: 'error', text: error.message });
    } finally {
      setIsLoading(false);
    }
  };

  // Formatar data
  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    const date = new Date(dateString);
    return date.toLocaleDateString('pt-BR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric'
    });
  };

  // Obter badge de role
  const getRoleBadge = (role) => {
    const badges = {
      'admin': { emoji: '👑', label: 'Admin', class: 'badge-admin' },
      'user': { emoji: '👤', label: 'Usuário', class: 'badge-user' }
    };
    return badges[role] || badges['user'];
  };

  // Obter badge de subscription
  const getSubscriptionBadge = (subscription) => {
    const badges = {
      'none': { emoji: '🆓', label: 'Free', class: 'badge-free' },
      'pro': { emoji: '⭐', label: 'Pro', class: 'badge-pro' },
      'business': { emoji: '💼', label: 'Business', class: 'badge-business' }
    };
    return badges[subscription] || badges['none'];
  };

  // Estatísticas
  const stats = useMemo(() => ({
    total: safeUsers.length,
    admins: safeUsers.filter(u => u.role === 'admin').length,
    pro: safeUsers.filter(u => u.subscription === 'pro').length,
    business: safeUsers.filter(u => u.subscription === 'business').length,
    free: safeUsers.filter(u => u.subscription === 'none').length
  }), [safeUsers]);

  return (
    <div className="users-container">
      {/* Header */}
      <div className="users-header">
        <div className="users-title-section">
          <h2>👥 Gerenciar Usuários</h2>
          <p>Administre todos os usuários do sistema</p>
        </div>
        <div style={{display:'flex', gap:12}}>
          <button className="btn-create" onClick={handleCreateClick}>
            <span>➕</span>
            Novo Usuário
          </button>
          <button className="btn-refresh" onClick={onRefresh}>
            <span className="refresh-icon">🔄</span>
            Atualizar
          </button>
        </div>
      </div>

      {/* Mensagem de sucesso/erro */}
      {message && (
        <div className={`users-message ${message.type}`}>
          {message.type === 'success' ? '✅' : '⚠️'} {message.text}
        </div>
      )}

      {/* Filtros */}
      <div className="users-filters">
        <div className="filter-search">
          <input
            type="text"
            placeholder="🔍 Buscar por nome, email, empresa ou ID..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
        </div>
        <div className="filter-selects">
          <select value={filterRole} onChange={(e) => setFilterRole(e.target.value)}>
            <option value="all">👤 Todos os papéis</option>
            <option value="admin">👑 Administradores</option>
            <option value="user">👤 Usuários</option>
          </select>
          <select value={filterSubscription} onChange={(e) => setFilterSubscription(e.target.value)}>
            <option value="all">💳 Todas as assinaturas</option>
            <option value="none">🆓 Free</option>
            <option value="pro">⭐ Pro</option>
            <option value="business">💼 Business</option>
          </select>
          <select value={sortBy} onChange={(e) => setSortBy(e.target.value)}>
            <option value="date-desc">📅 Mais recentes</option>
            <option value="date-asc">📅 Mais antigos</option>
            <option value="name">🔤 Por nome</option>
            <option value="email">✉️ Por email</option>
            <option value="role">👑 Por papel</option>
            <option value="subscription">💳 Por assinatura</option>
          </select>
        </div>
      </div>

      {/* Estatísticas */}
      <div className="users-stats">
        <div className="stat-card">
          <span className="stat-value">{stats.total}</span>
          <span className="stat-label">Total de Usuários</span>
        </div>
        <div className="stat-card">
          <span className="stat-value">{stats.admins}</span>
          <span className="stat-label">Administradores</span>
        </div>
        <div className="stat-card">
          <span className="stat-value">{stats.business}</span>
          <span className="stat-label">Business</span>
        </div>
        <div className="stat-card">
          <span className="stat-value">{stats.pro}</span>
          <span className="stat-label">Pro</span>
        </div>
        <div className="stat-card">
          <span className="stat-value">{stats.free}</span>
          <span className="stat-label">Free</span>
        </div>
      </div>

      {/* Tabela de Usuários */}
      {filteredAndSortedUsers.length === 0 ? (
        <div className="users-empty">
          <div className="empty-icon">👥</div>
          <h3>Nenhum usuário encontrado</h3>
          <p>Tente ajustar os filtros ou fazer uma nova busca.</p>
        </div>
      ) : (
        <div className="users-table-container">
          <table className="users-table">
            <thead>
              <tr>
                <th>Usuário</th>
                <th>Email</th>
                <th>Empresa</th>
                <th>Papel</th>
                <th>Plano</th>
                <th>Cadastro</th>
                <th>Ações</th>
              </tr>
            </thead>
            <tbody>
              {filteredAndSortedUsers.map(user => {
                const roleBadge = getRoleBadge(user.role);
                const subBadge = getSubscriptionBadge(user.subscription);
                const isCurrentUser = currentUserId && user.id === currentUserId;
                
                return (
                  <tr key={user.id} className={isCurrentUser ? 'current-user-row' : ''}>
                    <td>
                      <div className="user-info">
                        <div className="user-avatar">
                          {(user.name || user.email).charAt(0).toUpperCase()}
                        </div>
                        <div className="user-details">
                          <strong>
                            {user.name || 'Sem nome'}
                            {isCurrentUser && <span className="you-badge">Você</span>}
                          </strong>
                          {user.phone && <span className="user-phone">📱 {user.phone}</span>}
                        </div>
                      </div>
                    </td>
                    <td>{user.email}</td>
                    <td>{user.company || '—'}</td>
                    <td>
                      <span className={`role-badge ${roleBadge.class}`}>
                        {roleBadge.emoji} {roleBadge.label}
                      </span>
                    </td>
                    <td>
                      <span className={`subscription-badge ${subBadge.class}`}>
                        {subBadge.emoji} {subBadge.label}
                      </span>
                    </td>
                    <td>{formatDate(user.createdAt)}</td>
                    <td>
                      <div className="user-actions">
                        <button 
                          className="btn-action btn-edit" 
                          onClick={() => handleEditClick(user)}
                          title="Editar"
                        >
                          ✏️
                        </button>
                        <button 
                          className="btn-action btn-delete" 
                          onClick={() => handleDeleteClick(user)}
                          title={isCurrentUser ? "Você não pode deletar sua própria conta" : "Excluir"}
                          disabled={isCurrentUser}
                          style={isCurrentUser ? { opacity: 0.5, cursor: 'not-allowed' } : {}}
                        >
                          🗑️
                        </button>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {/* Modal de Edição */}
      {showEditModal && (
        <div className="modal-overlay" onClick={() => setShowEditModal(false)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <h3>✏️ Editar Usuário</h3>
            <div className="modal-form">
              <div className="form-group">
                <label>Nome</label>
                <input
                  type="text"
                  value={editForm.name}
                  onChange={(e) => setEditForm({ ...editForm, name: e.target.value })}
                  placeholder="Nome do usuário"
                />
              </div>
              <div className="form-group">
                <label>Email</label>
                <input
                  type="email"
                  value={editForm.email}
                  onChange={(e) => setEditForm({ ...editForm, email: e.target.value })}
                  placeholder="email@exemplo.com"
                />
              </div>
              <div className="form-row">
                <div className="form-group">
                  <label>Telefone</label>
                  <input
                    type="text"
                    value={editForm.phone}
                    onChange={(e) => setEditForm({ ...editForm, phone: e.target.value })}
                    placeholder="(00) 00000-0000"
                  />
                </div>
                <div className="form-group">
                  <label>Empresa</label>
                  <input
                    type="text"
                    value={editForm.company}
                    onChange={(e) => setEditForm({ ...editForm, company: e.target.value })}
                    placeholder="Nome da empresa"
                  />
                </div>
              </div>
              <div className="form-row">
                <div className="form-group">
                  <label>Papel</label>
                  <select
                    value={editForm.role}
                    onChange={(e) => setEditForm({ ...editForm, role: e.target.value })}
                  >
                    <option value="user">👤 Usuário</option>
                    <option value="admin">👑 Administrador</option>
                  </select>
                </div>
                <div className="form-group">
                  <label>Plano</label>
                  <select
                    value={editForm.subscription}
                    onChange={(e) => setEditForm({ ...editForm, subscription: e.target.value })}
                  >
                    <option value="none">🆓 Free</option>
                    <option value="pro">⭐ Pro</option>
                    <option value="business">💼 Business</option>
                  </select>
                </div>
              </div>
            </div>
            <div className="modal-actions">
              <button 
                className="btn-modal-cancel" 
                onClick={() => setShowEditModal(false)}
                disabled={isLoading}
              >
                Cancelar
              </button>
              <button 
                className="btn-modal-confirm" 
                onClick={handleUpdateUser}
                disabled={isLoading}
              >
                {isLoading ? <span className="btn-spinner"></span> : 'Salvar'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Modal de Criação */}
      {showCreateModal && (
        <div className="modal-overlay" onClick={() => setShowCreateModal(false)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <h3>➕ Criar Novo Usuário</h3>
            <div className="modal-form">
              <div className="form-group">
                <label>Nome *</label>
                <input
                  type="text"
                  value={createForm.name}
                  onChange={(e) => setCreateForm({ ...createForm, name: e.target.value })}
                  placeholder="Nome completo"
                />
              </div>
              <div className="form-group">
                <label>Email *</label>
                <input
                  type="email"
                  value={createForm.email}
                  onChange={(e) => setCreateForm({ ...createForm, email: e.target.value })}
                  placeholder="email@exemplo.com"
                  required
                />
              </div>
              <div className="form-group">
                <label>Senha *</label>
                <input
                  type="password"
                  value={createForm.password}
                  onChange={(e) => setCreateForm({ ...createForm, password: e.target.value })}
                  placeholder="Senha (mínimo 6 caracteres)"
                  required
                />
              </div>
              <div className="form-row">
                <div className="form-group">
                  <label>Telefone</label>
                  <input
                    type="text"
                    value={createForm.phone}
                    onChange={(e) => setCreateForm({ ...createForm, phone: e.target.value })}
                    placeholder="(00) 00000-0000"
                  />
                </div>
                <div className="form-group">
                  <label>Empresa</label>
                  <input
                    type="text"
                    value={createForm.company}
                    onChange={(e) => setCreateForm({ ...createForm, company: e.target.value })}
                    placeholder="Nome da empresa"
                  />
                </div>
              </div>
              <div className="form-row">
                <div className="form-group">
                  <label>Papel</label>
                  <select
                    value={createForm.role}
                    onChange={(e) => setCreateForm({ ...createForm, role: e.target.value })}
                  >
                    <option value="user">👤 Usuário</option>
                    <option value="admin">👑 Administrador</option>
                  </select>
                </div>
                <div className="form-group">
                  <label>Plano</label>
                  <select
                    value={createForm.subscription}
                    onChange={(e) => setCreateForm({ ...createForm, subscription: e.target.value })}
                  >
                    <option value="none">🆓 Free</option>
                    <option value="pro">⭐ Pro</option>
                    <option value="business">💼 Business</option>
                  </select>
                </div>
              </div>
            </div>
            <div className="modal-actions">
              <button 
                className="btn-modal-cancel" 
                onClick={() => setShowCreateModal(false)}
                disabled={isLoading}
              >
                Cancelar
              </button>
              <button 
                className="btn-modal-confirm" 
                onClick={handleCreateUser}
                disabled={isLoading}
              >
                {isLoading ? <span className="btn-spinner"></span> : 'Criar Usuário'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Modal de Confirmação de Exclusão */}
      {showDeleteModal && (
        <div className="modal-overlay" onClick={() => setShowDeleteModal(false)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <h3>⚠️ Confirmar Exclusão</h3>
            <p>Tem certeza que deseja excluir este usuário?</p>
            {selectedUser && (
              <div className="modal-user-info">
                <strong>Nome:</strong> {selectedUser.name || 'Sem nome'}
                <br />
                <strong>Email:</strong> {selectedUser.email}
                <br />
                <strong>Cadastro:</strong> {formatDate(selectedUser.createdAt)}
              </div>
            )}
            <p className="warning-text">⚠️ Esta ação não pode ser desfeita!</p>
            <div className="modal-actions">
              <button 
                className="btn-modal-cancel" 
                onClick={() => setShowDeleteModal(false)}
                disabled={isLoading}
              >
                Cancelar
              </button>
              <button 
                className="btn-modal-confirm btn-danger" 
                onClick={confirmDelete}
                disabled={isLoading}
              >
                {isLoading ? <span className="btn-spinner"></span> : 'Sim, excluir'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default DashboardUsers;
